import type { LoanTerms } from '../../calculatedAttributes/store';
import { PrefixCurrencyType } from '../../components/ProductCard/constants';
import {
  CmsOffer,
  CmsProduct,
  CriteriaNumberRange,
  GiftListItem,
} from '../../types/cmsProduct';
import { filterGiftsByAmountAndTenure } from './attrHelpers';
import { calculateOffersValue } from './calculateRewardValue';
import { currencyFormat, formatCurrencyAmount } from './formatters';
import { filterOffersByAmountAndTenure } from './offers';

/**
 * The reward a personal-loan reader gets at a given loan amount and tenure
 * (CMS-1922 R3, reused by the Quick Answer in CMS-1928): Table Builder and Quick
 * Answer must show the same number, so both read it here.
 *
 * Offers arrive already filtered for expiry by the fe-services mapper, which
 * keeps everything here date-free and SSR-safe.
 */

/** The two reward sources, in the order the reader sees them (R2). */
export type LoanRewardOffers = {
  exclusiveOffers: CmsOffer[];
  welcomeOffers: CmsOffer[];
};

export type LoanRewardBreakdown = {
  /** Sum of the two subtotals — what the pill shows. */
  total: number;
  /** Absent when the source has nothing qualifying, so it drops out of the breakdown. */
  exclusive?: number;
  provider?: number;
  currencyPrefix?: PrefixCurrencyType;
};

/**
 * A product's offers as the RP counts them: bundle exclusives are exclusives, and
 * the Hot Reward Pick never enters it (CMS-1466).
 */
export const productLoanRewardOffers = (
  product: CmsProduct | undefined,
): LoanRewardOffers => ({
  exclusiveOffers: [
    ...(product?.exclusive_offers ?? []),
    ...(product?.bundle_exclusive_offers ?? []),
  ],
  welcomeOffers: product?.welcome_offers ?? [],
});

/** Whether a gift's eligibility band covers the terms — the RP's own check. */
export const isGiftForLoan = (item: GiftListItem, terms: LoanTerms): boolean =>
  filterGiftsByAmountAndTenure([item], terms.amount, terms.tenure)
    .matchingAmountTenure.length > 0;

/**
 * A source's offers with only the gifts that qualify at the terms — the RP's own
 * filter, so a tier the RP would not offer never counts here either.
 */
export const qualifyingLoanOffers = (
  offers: CmsOffer[],
  terms: LoanTerms,
): CmsOffer[] =>
  filterOffersByAmountAndTenure(
    offers,
    String(terms.amount),
    String(terms.tenure),
  );

/**
 * One source's reward: per offer the qualifying gift worth the most, summed — the
 * RP's `calculateOffersValue`. Top-up is deducted for the exclusive source only
 * (R3, per CMS-1023). `undefined` when nothing qualifies.
 */
const sourceSubtotal = (
  offers: CmsOffer[],
  terms: LoanTerms,
  deductTopUp: boolean,
): { subtotal: number; prefix?: PrefixCurrencyType } | undefined => {
  const qualifying = qualifyingLoanOffers(offers, terms);
  const subtotal = calculateOffersValue(qualifying, { deductTopUp });
  if (subtotal <= 0) return undefined;

  const prefix = qualifying
    .flatMap((offer) => offer.list_of_gifts ?? [])
    .find((item) => item.gift?.gift_value?.prefix)?.gift?.gift_value?.prefix;
  return { subtotal, prefix };
};

/**
 * A breakdown amount as the reader sees it. The gift values carry their own
 * currency; the market's covers an offer authored without one.
 */
export const formatLoanRewardAmount = (
  value: number,
  breakdown: Pick<LoanRewardBreakdown, 'currencyPrefix'>,
  marketCurrency: string,
): string =>
  formatCurrencyAmount(
    value,
    currencyFormat(breakdown.currencyPrefix) || marketCurrency,
  );

/**
 * The combined reward and its per-source split at these terms. `undefined` when
 * nothing qualifies in either source — the cell then shows "-" and nothing else.
 */
export const getLoanRewardBreakdown = (
  offers: LoanRewardOffers,
  terms: LoanTerms | undefined,
): LoanRewardBreakdown | undefined => {
  if (!terms) return undefined;

  const exclusive = sourceSubtotal(offers.exclusiveOffers, terms, true);
  const provider = sourceSubtotal(offers.welcomeOffers, terms, false);
  if (!exclusive && !provider) return undefined;

  return {
    total: (exclusive?.subtotal ?? 0) + (provider?.subtotal ?? 0),
    exclusive: exclusive?.subtotal,
    provider: provider?.subtotal,
    currencyPrefix: exclusive?.prefix ?? provider?.prefix,
  };
};

/** One eligibility band and the gifts that share it. */
export type LoanRewardTier = {
  /** The band's identity, used as the React key. */
  key: string;
  criteria?: CriteriaNumberRange[];
  giftTitles: string[];
};

export type LoanRewardTierSection = {
  /** The band(s) covering the terms — the highlight box. */
  current: LoanRewardTier[];
  other: LoanRewardTier[];
};

const bandKey = (criteria: CriteriaNumberRange[] | undefined): string =>
  (criteria ?? [])
    .map((c) => `${c.name?.name}:${c.value_start ?? ''}-${c.value_end ?? ''}`)
    .sort()
    .join('|');

/**
 * One source's gifts grouped by eligibility band and split into the band the
 * terms fall in and the rest (R4). Grouped by band, not by offer: two offers
 * paying at the same amount and tenure read as one tier to the reader.
 *
 * `titleOf` lets a caller swap in the title it shows elsewhere — the Hot Reward
 * Pick cell's authored overrides.
 */
export const getLoanRewardTierSection = (
  offers: CmsOffer[],
  terms: LoanTerms | undefined,
  titleOf: (item: GiftListItem) => string | undefined = (item) =>
    item.gift?.gift_title,
): LoanRewardTierSection => {
  const bands = new Map<string, LoanRewardTier & { current: boolean }>();

  for (const offer of offers) {
    for (const item of offer.list_of_gifts ?? []) {
      const title = titleOf(item);
      if (!title) continue;

      const key = bandKey(item.criteria_list);
      const band = bands.get(key);
      if (band) {
        band.giftTitles.push(title);
        continue;
      }
      bands.set(key, {
        key,
        criteria: item.criteria_list,
        giftTitles: [title],
        // No terms → nothing is highlighted rather than one band arbitrarily.
        current: !!terms && isGiftForLoan(item, terms),
      });
    }
  }

  const all = [...bands.values()];
  const strip = ({
    current: _,
    ...tier
  }: LoanRewardTier & { current: boolean }) => tier;
  return {
    current: all.filter((band) => band.current).map(strip),
    other: all.filter((band) => !band.current).map(strip),
  };
};
